// Spillets motor i browseren: fast 10 Hz sim med akkumulator, intent-kø, fart og pause.
// Alt hvad spilleren gør, går som intents gennem samme vej som Volt og botten.

import { getLevel } from './content/levels';
import { applyNow, allowedInPause, alwaysInPause, createState, difficulty, isQuiet, step, TICK_MS } from './sim';
import type { ActionKind, ApplyError, CreateOptions, DifficultyId, Intent, IntentSource, LevelDef, PlayerId, SimEvent, State } from './sim';
import { check } from './sim/actions';
import { roleBlocked } from './sim/roles';
import { lineById, UNDO_TICKS } from './sim/grid';

export type Speed = 1 | 2 | 3;

/** Auto-spol: så mange stille ticks i træk, før farten går op. */
export const SPOL_QUIET_TICKS = 30;
/** Auto-spol: opramping fra ×1 til ×3. */
export const SPOL_RAMP_MS = 500;
export const SPOL_MAX = 3;

/**
 * Auto-spol: når der ingenting sker, spoler soloespillet selv frem til ×3. Det er ren visning af
 * tiden: sim'en får de samme ticks, bare hurtigere. Farten falder til ×1 i samme tick, som noget
 * nyt dukker op (en by, et varsel, et mørkeur, en front inden for 10 s), og ved ethvert tryk.
 */
export class AutoSpol {
  /** Må auto-spol køre lige nu? Sættes af main hver frame (solo, ingen skærm, ingen lektion). */
  allowed = true;
  /** Slået fra helt (?spol=0, til målinger). */
  disabled = false;
  private quiet = 0;
  private on = false;
  private ramp = 0;

  /** Efter hvert tick: tæl stille ticks, eller slip farten med det samme. */
  tick(state: State, evs: readonly SimEvent[]): void {
    if (evs.some(dropsSpol) || !isQuiet(state)) {
      this.drop();
      return;
    }
    this.quiet++;
    if (this.quiet >= SPOL_QUIET_TICKS && this.allowed && !this.disabled) this.on = true;
  }

  /** Spilleren rørte ved noget: tilbage til ×1, og roen tælles forfra. */
  drop(): void {
    this.quiet = 0;
    this.on = false;
    this.ramp = 0;
  }

  /** Hver frame (realtid): rampen op mod ×3. */
  frame(dtMs: number): void {
    if (!this.allowed || this.disabled) {
      if (this.on || this.ramp > 0) this.drop();
      return;
    }
    if (this.on) this.ramp = Math.min(1, this.ramp + dtMs / SPOL_RAMP_MS);
  }

  /** Kører auto-spol (også mens den ramper op)? */
  get active(): boolean {
    return this.on;
  }

  /** Farten fra auto-spol: 1 når den er slukket, op til 3. */
  get rate(): number {
    if (!this.on) return 1;
    const e = 1 - (1 - this.ramp) * (1 - this.ramp);
    return 1 + (SPOL_MAX - 1) * e;
  }
}

/** Hændelser, der altid slår auto-spol fra med det samme. */
export function dropsSpol(e: SimEvent): boolean {
  return e.t === 'node_appeared' || e.t === 'warning' || e.t === 'clock_started' || e.t === 'smaa' || e.t === 'line_broken' || e.t === 'blackout' || e.t === 'year_end';
}

export interface GameHooks {
  /** Lige før hvert tick (til interpolation). */
  beforeTick(state: State): void;
  /** Hændelser fra et tick eller en handling i pause. */
  events(evs: SimEvent[], state: State): void;
  /** En handling blev afvist før den nåede sim'en. */
  rejected(kind: ActionKind, error: ApplyError): void;
}

export type PushResult = { ok: true; cost: number } | { ok: false; error: ApplyError | 'paused' };

/** Online (src/net/online.ts): intents går til Room-DO'en, og ticks kommer tilbage som ops. */
export interface Remote {
  send(intent: Intent): void;
  /** Efter hvert afspillet op: hvor mange ops der nu er udført (til hash-tjek). */
  applied(n: number, state: State): void;
}

/** Et op fra rummet: et tick (true) eller handlinger udført i pause (false). */
export interface RemoteOp {
  tick: boolean;
  intents: Intent[];
}

/** Står flere ticks i kø end dette, spoles der frem, så vi ikke halter efter makkeren. */
const REMOTE_LAG = 6;

export class Game {
  state: State;
  level: LevelDef;
  speed: Speed = 1;
  /** Auto-spol i stille perioder (kun solo; online kører altid rummets fart). */
  readonly spol = new AutoSpol();
  /** Spilleren har sat på pause (eller fanen mistede fokus). */
  paused = false;
  /** Pausen er spillets egen korte hændelsespause (Lærling), ikke spillerens. Sættes af UI'et. */
  softPause = false;
  /** Pause for en skærm (banestart). Tæller ikke som spillerens pause. */
  held = true;
  /**
   * Solo: et hændelseskort venter på svar, og uret står stille imens (Spor: kortet). Ikke spillerens
   * pause: kortet svarer man på, og bygge kan man først igen, når tiden går (som i pausen, efter
   * sværheden). Sættes af UI'et; autoplay og makkerrum sætter det aldrig.
   */
  cardHold = false;
  /**
   * Makkerrum (Spor: tillid): et nyt år venter på, at I begge siger klar. ready er dem, der har trykket
   * afspil; goAt (performance.now) er, hvornår året starter af sig selv. Sættes af Online.
   */
  yearWait: { ready: ('p1' | 'p2')[]; goAt: number | null } | null = null;
  private acc = 0;
  private seq = 0;
  private queue: Intent[] = [];
  /** Seneste ledning, spilleren selv byggede (til fortryd). */
  private lastBuilt: string | null = null;
  /** Tæller spillerens egne handlinger, der gik igennem tjekket (hintet holder sit råd, til man gør noget). */
  acts = 0;
  /** Kaldes før hvert tick af autoplay (Volt eller bot). */
  brain: ((state: State) => Intent | null) | null = null;
  /**
   * Testkrog (scripts/tillidtest.mjs): en spøgelsesspiller, der trykker som et menneske. Trækkene går
   * ad spillerens egen vej (push, source 'human'), så hintet, træneren og `acts` opfører sig som med
   * en rigtig spiller. Modsat `brain` slukker den ikke hintet.
   */
  ghost: ((state: State) => Intent | null) | null = null;
  /** Hvem der sidder ved denne skærm. Co-op giver den anden spiller 'p2'. */
  player: PlayerId = 'p1';
  /** Online med roller, og begge er forbundet: rummet afviser den andens handlinger (sættes af online.ts). */
  rolesLive = false;

  /** Er handlingen makkerens job lige nu (IGN-løkke runde 4)? */
  theirs(kind: ActionKind): boolean {
    return this.rolesLive && roleBlocked(this.state, kind, this.player);
  }

  /** Online: rummet er sandheden, og denne skærm afspiller kun, hvad det sender. */
  remote: Remote | null = null;
  private ops: RemoteOp[] = [];
  /** Ops udført siden banestart (rummets tæller). */
  opN = 0;
  /** Solo: tilstanden ved seneste årsstart, så et tabt år kan prøves igen uden at starte forfra. */
  checkpoint: State | null = null;

  constructor(
    levelId: string,
    diff: DifficultyId,
    seed: number,
    private hooks: GameHooks,
  ) {
    this.state = createState(levelId, diff, seed);
    this.level = getLevel(levelId);
  }

  start(levelId: string, diff: DifficultyId, seed: number, opts: CreateOptions = {}): void {
    this.state = createState(levelId, diff, seed, opts);
    this.p2Seq = 0;
    this.level = getLevel(levelId);
    this.queue = [];
    this.acc = 0;
    this.lastBuilt = null;
    this.paused = false;
    this.held = true;
    this.cardHold = false;
    this.yearWait = null;
    this.checkpoint = null;
    this.spol.drop();
  }

  /** Spol tilbage til en gemt tilstand (samme bane, samme frø). */
  restore(state: State): void {
    this.state = state;
    this.queue = [];
    this.acc = 0;
    this.lastBuilt = null;
    this.paused = false;
    this.held = true;
    this.cardHold = false;
    this.spol.drop();
  }

  get running(): boolean {
    return !this.paused && !this.held && !this.cardHold && this.state.phase !== 'won' && this.state.phase !== 'lost';
  }

  get over(): boolean {
    return this.state.phase === 'won' || this.state.phase === 'lost';
  }

  /** Må man bygge lige nu? Under pause kun på sværheder, der tillader det. */
  get canBuildNow(): boolean {
    if (this.over || this.state.phase !== 'playing') return false;
    if (this.paused || this.cardHold) return difficulty(this.state.difficulty).buildInPause;
    return true;
  }

  /** Må denne handling gøres i pause (svar på kort, gave, montør altid; resten efter sværheden)? */
  canInPause(kind: ActionKind): boolean {
    return !this.over && allowedInPause(kind, difficulty(this.state.difficulty));
  }

  /** Læg en handling i køen. Tjekkes først, så UI'et kan svare med det samme. */
  push(kind: ActionKind, target?: string, opt?: string, source: IntentSource = 'human'): PushResult {
    const c = check(this.state, kind, target, opt, this.player);
    if (!c.ok) {
      this.hooks.rejected(kind, c.error);
      return c;
    }
    if (this.theirs(kind)) {
      this.hooks.rejected(kind, 'other_role');
      return { ok: false, error: 'other_role' };
    }
    if ((this.paused || this.cardHold) && !allowedInPause(kind, difficulty(this.state.difficulty))) return { ok: false, error: 'paused' };
    if (source === 'human') this.acts++;
    const i: Intent = { player: this.player, clientSeq: ++this.seq, kind, source };
    if (target !== undefined) i.target = target;
    if (opt !== undefined) i.opt = opt;
    if (this.remote) {
      // Rummet afgør det. UI'et viser et spøgelse, til handlingen kommer tilbage som et op.
      this.remote.send(i);
      return c;
    }
    if (!this.running && !this.over) {
      // I pause (eller ved årsskifte på en skærm) udføres handlingen straks, men ad samme vej som
      // et tick: idempotent på (spiller, clientSeq), så Room-DO'en kan gøre præcis det samme.
      const evs = applyNow(this.state, [i]);
      const rej = evs.find((e) => e.t === 'rejected' && e.player === this.player);
      if (rej && rej.t === 'rejected') {
        this.hooks.rejected(kind, rej.error);
        return { ok: false, error: rej.error };
      }
      this.noteBuilt(evs, source);
      this.hooks.events(evs, this.state);
      return c;
    }
    this.queue.push(i);
    return c;
  }

  /**
   * Orkans plan (Spor: tillid): trækkene fra pausen udføres ved afspil, ét ad gangen i den rækkefølge,
   * de blev lagt, mod nettet som det står efter de forrige. Samme vej som i forhåndsvisningen (applyNow),
   * så det, planen viste, også er det, der sker: stationen står, før ledningen ind til den bygges.
   * Før lå trækkene i tick-køen og blev tjekket hver for sig mod nettet uden de forrige, og en ledning
   * ind i en by, hvis station stod i samme kø, forsvandt uden et ord. Svarer med fejlen pr. træk
   * (null, når det lykkedes); UI'et siger selv, hvad der ikke kunne lade sig gøre.
   */
  applyPlan(items: readonly { kind: ActionKind; target?: string; opt?: string }[]): (ApplyError | null)[] {
    const out: (ApplyError | null)[] = [];
    for (const p of items) {
      if (this.over || this.remote || this.state.phase !== 'playing') {
        out.push('wrong_phase');
        continue;
      }
      const c = check(this.state, p.kind, p.target, p.opt, this.player);
      if (!c.ok) {
        out.push(c.error);
        continue;
      }
      const i: Intent = { player: this.player, clientSeq: ++this.seq, kind: p.kind, source: 'human' };
      if (p.target !== undefined) i.target = p.target;
      if (p.opt !== undefined) i.opt = p.opt;
      const evs = applyNow(this.state, [i]);
      const rej = evs.find((e) => e.t === 'rejected' && e.player === this.player);
      if (rej && rej.t === 'rejected') {
        out.push(rej.error);
        continue;
      }
      this.acts++;
      this.noteBuilt(evs, 'human');
      this.hooks.events(evs, this.state);
      out.push(null);
    }
    return out;
  }

  private noteBuilt(evs: SimEvent[], source: IntentSource): void {
    if (source !== 'human') return;
    for (const e of evs) {
      if (e.t === 'line_built' && e.by === this.player) this.lastBuilt = e.line;
      if (e.t === 'line_removed' && e.line === this.lastBuilt) this.lastBuilt = null;
    }
  }

  /** Den ledning, der kan fortrydes nu (fuld refusion), eller null. */
  get undoable(): string | null {
    const id = this.lastBuilt;
    if (!id) return null;
    const l = lineById(this.state, id);
    if (!l || this.state.tick - l.builtTick > UNDO_TICKS) return null;
    return id;
  }

  /** Ledningen, et fortryd er ved at rive ned (så UI'et kan sige "Fortrudt" frem for at advare). */
  undoing: string | null = null;

  undo(): boolean {
    // Står ledningen stadig i køen (trukket lige før tick'et), tages den bare ud igen.
    for (let k = this.queue.length - 1; k >= 0; k--) {
      const q = this.queue[k] as Intent;
      if (q.kind === 'build_line' && q.source === 'human' && q.player === this.player) {
        this.queue.splice(k, 1);
        return true;
      }
    }
    const id = this.undoable;
    if (!id) return false;
    const r = this.push('demolish', id);
    if (r.ok) {
      this.lastBuilt = null;
      this.undoing = id;
    }
    return r.ok;
  }

  private p2Seq = 0;

  /**
   * En handling fra den anden plads (Volt som makker). Samme vej som spillerens egen push: mens
   * uret går, i køen til næste tick; står uret stille (årsskifte, kort, pause), udføres det, der
   * altid må gøres i pause (gave, svar på kort, montør), straks med applyNow. Volt bygger aldrig
   * i pause. Før IGN-løkke 2 lå de i en kø, der aldrig blev tømt, fordi uret stod stille, og
   * spillet frøs (B1/B2).
   * Egen sekvens, så idempotensen gælder pr. spiller.
   */
  pushAs(player: PlayerId, kind: ActionKind, target: string | undefined, opt: string | undefined, source: IntentSource): PushResult {
    if (this.over) return { ok: false, error: 'paused' };
    const stillNow = !this.running;
    if (stillNow && !alwaysInPause(kind)) return { ok: false, error: 'paused' };
    const c = check(this.state, kind, target, opt, player);
    if (!c.ok) return c;
    const i: Intent = { player, clientSeq: ++this.p2Seq, kind, source };
    if (target !== undefined) i.target = target;
    if (opt !== undefined) i.opt = opt;
    if (stillNow && !this.remote) {
      const evs = applyNow(this.state, [i]);
      const rej = evs.find((e) => e.t === 'rejected' && e.player === player);
      if (rej && rej.t === 'rejected') return { ok: false, error: rej.error };
      this.hooks.events(evs, this.state);
      return c;
    }
    this.queue.push(i);
    return c;
  }

  /** Ét sim-tick med køen og evt. autoplay. */
  tick(): void {
    if (this.over) return;
    if (this.brain) {
      const i = this.brain(this.state);
      if (i) {
        i.clientSeq = ++this.seq;
        this.queue.push(i);
      }
    }
    if (this.ghost && !this.brain) {
      const g = this.ghost(this.state);
      if (g) this.push(g.kind, g.target, g.opt, 'human');
    }
    const intents = this.queue;
    this.queue = [];
    this.hooks.beforeTick(this.state);
    const evs = step(this.state, intents);
    const humans = intents.some((i) => i.source === 'human');
    if (humans) this.noteBuilt(evs, 'human');
    this.spol.tick(this.state, evs);
    this.hooks.events(evs, this.state);
  }

  /** Den fart, uret går med lige nu: spillerens valg, eller auto-spol når det er hurtigere. */
  get rate(): number {
    if (this.state.phase === 'yearend') return 1;
    return Math.max(this.speed, this.remote ? 1 : this.spol.rate);
  }

  // -------------------------------------------------------------------------
  // Online

  /** Overtag rummets state (start, genforbindelse eller hash-forskel). */
  loadRemote(state: State, n: number, lastSeq: number): void {
    this.state = state;
    this.level = getLevel(state.levelId);
    this.ops = [];
    this.opN = n;
    this.acc = 0;
    this.queue = [];
    this.seq = Math.max(this.seq, lastSeq, state.lastSeq[this.player] ?? 0);
    this.lastBuilt = null;
  }

  /** Næste clientSeq må aldrig gå under det, rummet allerede har set (efter genforbindelse). */
  seqAtLeast(n: number): void {
    this.seq = Math.max(this.seq, n);
  }

  enqueue(ops: RemoteOp[]): void {
    for (const o of ops) this.ops.push(o);
  }

  /** Ops, der venter på at blive afspillet. */
  get queued(): number {
    return this.ops.length;
  }

  /** Smid køen (før et frisk snapshot, fx efter lang tid i baggrunden). */
  dropOps(): void {
    this.ops = [];
  }

  /** Ticks, der venter på at blive afspillet. */
  get pendingTicks(): number {
    let n = 0;
    for (const o of this.ops) if (o.tick) n++;
    return n;
  }

  private applyRemote(op: RemoteOp): void {
    if (op.tick) {
      this.hooks.beforeTick(this.state);
      const evs = step(this.state, op.intents);
      this.noteBuilt(evs, 'human');
      this.hooks.events(evs, this.state);
    } else {
      const evs = applyNow(this.state, op.intents);
      this.noteBuilt(evs, 'human');
      this.hooks.events(evs, this.state);
    }
    this.opN++;
    this.remote?.applied(this.opN, this.state);
  }

  /**
   * Afspil rummets ops i takt med uret. Handlinger i pause udføres straks; ticks afspilles med
   * 10 Hz (gange farten), og en lille buffer udjævner, at de kommer i bundter hvert 200 ms.
   */
  private advanceRemote(dtMs: number): number {
    const speed = this.state.phase === 'yearend' ? 1 : this.speed;
    this.acc += Math.min(300, dtMs) * speed;
    let ticks = this.pendingTicks;
    let extra = ticks > REMOTE_LAG ? ticks - 2 : 0;
    let n = 0;
    while (this.ops.length > 0 && n < 60) {
      const op = this.ops[0] as RemoteOp;
      if (!op.tick) {
        this.ops.shift();
        this.applyRemote(op);
        continue;
      }
      if (extra > 0) extra--;
      else if (this.acc >= TICK_MS) this.acc -= TICK_MS;
      else break;
      this.ops.shift();
      this.applyRemote(op);
      ticks--;
      n++;
    }
    // Ingen flere ticks i kø: vent på næste bundt i stedet for at spare tid op.
    if (ticks === 0) this.acc = Math.min(this.acc, TICK_MS);
    return Math.max(0, Math.min(1, this.acc / TICK_MS));
  }

  /** Real tid ind, antal ticks ud. Returnerer interpolationen 0..1 mellem ticks. */
  advance(dtMs: number): number {
    if (this.remote) return this.advanceRemote(dtMs);
    if (!this.running) {
      this.acc = 0;
      return 1;
    }
    // Årsskiftet kører i normal fart, så valget ikke haster.
    this.spol.frame(dtMs);
    let speed = this.rate;
    this.acc += Math.min(300, dtMs) * speed;
    let n = 0;
    while (this.acc >= TICK_MS && n < 12) {
      this.acc -= TICK_MS;
      this.tick();
      n++;
      if (!this.running) {
        this.acc = 0;
        break;
      }
      // Slap auto-spol farten i dette tick, går resten af billedets tid i den nye fart: næste tick
      // kommer først efter et helt tick i ×1.
      const now = this.rate;
      if (now < speed) {
        this.acc *= now / speed;
        speed = now;
      }
    }
    if (n >= 12) this.acc = 0;
    return Math.max(0, Math.min(1, this.acc / TICK_MS));
  }

  /** Spol frem uden at tegne (debug og playtest). */
  stepSeconds(seconds: number): void {
    const n = Math.round(seconds * 10);
    for (let i = 0; i < n && !this.over; i++) this.tick();
  }
}
